import { memo, useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { BodyCopy } from "../components/BodyCopy";
import { FirstSendWarning } from "../components/first-send/FirstSendWarning";
import { useFirstSendWarning } from "../hooks/useFirstSendWarning";
import { Toast } from "../components/Toast";
import {
  BankIcon,
  Button,
  CashSpecialIcon,
  ExchangeIcon,
  Layout,
  SupportIcon,
  Title,
  WalletIcon,
  WarningIcon,
} from "lola-framework-ui-test";
import { AuraLayout } from "../components/AuraLayout";
import { FlagCircle } from "../components/FlagCircle";
import { QuoteInfo } from "../components/QuoteInfo";
import { PayoutInfo } from "../components/PayoutInfo";
import { FooterDetailSummary } from "../components/FooterDetailSummary";
import { Popup } from "../components/Popup";
import { RouteUnavailablePage } from "./RouteUnavailablePage";
import {
  SelectFundingMethodSlot,
  type FlatFundingPick,
} from "../components/SelectFundingMethodSlot";
import { SelectCountrySlot } from "../components/SelectCountrySlot";
import { SelectDestinationCountrySlot } from "../components/SelectDestinationCountrySlot";
import { AmountOutOfRangeInfo } from "../components/AmountOutOfRangeInfo";
import { APP_ROUTES, TITLE_COLOR } from "../constants";
import { useStore } from "../store/globalState";
import { useSessionStore } from "../store/useSessionStore";
import { useRequestStore } from "../store/useRequestStore";
import { useRouteWithToken } from "../hooks/useRouteWithToken";
import { useClaimLink } from "../hooks/useClaimLink";
import { usePreQuote } from "../hooks/usePreQuote";
import { useQuoteLoadingTimedOut } from "../hooks/useQuoteLoadingTimedOut";
import { useFundingMethodLimits } from "../hooks/useFundingMethodLimits";
import { usePaymentMethods } from "../hooks/usePaymentMethods";
import { paymentType } from "../interfaces/payment";
import { UnavailableFundingCardSheet } from "../components/UnavailableFundingCardSheet";
import type { ICardPaymentMethod } from "../interfaces/payment-method";
import { isCardFundingAvailable } from "../utils/card-funding-availability";
import {
  isFundingCardBlocked,
  resolveFundingCardFromState,
} from "../utils/resolve-funding-card";
import {
  catalogFundingMethodOf,
  type AddCardClassNotice,
} from "../utils/resolve-add-card-funding-class";
import {
  availableFundingMethodsFromDiscovery,
  bestCustomerFeeForFundingMethod,
  categoryFromFundingMethod,
  FUNDING_METHODS,
  maxTotalDebit,
  optionForFundingMethod,
  resolveFundingMethod,
  type FundingMethod,
} from "../utils/catalog";
import {
  countryOptionsFromCodes,
  currencyForCountryCode,
  expandCorridorDestinationOptions,
  findCorridor,
  pickDefaultDestinationCurrency,
  seedDestinationCountry,
  seedOriginCountry,
  supportedDestinations,
  supportedOrigins,
} from "../utils/corridors";
import { fundingMethodRequiresInstrument } from "../utils/fundingInstrument";
import { isFundingInstrumentReady } from "../interfaces/funding";
import { amountFormatter } from "../utils";
import { resolveComposeSendAmount } from "../utils/resolveComposeSendAmount";
import {
  compareSendToBound,
  corridorsForFundingMethod,
  skusForCorridors,
} from "../utils/fundingLimits";
import masterCardLogo from "../assets/master_card_icon.svg";
import visaLogo from "../assets/visa_color_icon.svg";
import type { PreQuoteInput } from "../api/pricing";
import { getEvents, safeCapture } from "../analytics";
import {
  useBlockedRailCards,
  type RailUnavailableNotice,
} from "../utils/blocked-rail-cards";
import { useSupportSheet } from "../hooks/useSupportSheet";
import { ScrollFadeIndicator } from "../components/ScrollFadeIndicator";
import { accentInkOnSurface } from "../utils/accentInkOnSurface";
import { Navbar } from "../components/Navbar";


/**
 * `primaryMesh` is usually a gradient string, which is invalid for `color` and
 * `border-color`. The outlined icon in the rate pill needs a solid value, so it
 * falls back to the gradient's first stop.
 */
const solidStop = (color: string) =>
  color.match(/#[0-9a-f]{3,8}|rgba?\([^)]*\)/i)?.[0] ?? color;

const sheetCardFromNotice = (
  card: AddCardClassNotice["card"]
): ICardPaymentMethod => ({
  paymentMethodId: card.paymentMethodId,
  brand: card.brand,
  last4: card.last4,
  expiryMonth: "",
  expiryYear: "",
  enabled: true,
  isDefault: false,
  fundingMethod: card.fundingMethod,
  productClass: card.productClass,
});

/**
 * Send-origination compose screen: sender pins send amount + funding method,
 * then continues to the recipient identity step.
 */
export const TransferPage = memo(() => {
  useClaimLink();
  const { t } = useTranslation();
  const location = useLocation();
  const cardClassNotice = (
    location.state as { cardClassNotice?: AddCardClassNotice } | null
  )?.cardClassNotice;
  const railNotice = (
    location.state as { railUnavailable?: RailUnavailableNotice } | null
  )?.railUnavailable;
  const blockedRailCards = useBlockedRailCards();
  const cardClassNoticeHandledRef = useRef(false);

  useEffect(() => {
    safeCapture(getEvents().SCREEN_VIEW("route_amount"));
    safeCapture(getEvents().SCREEN_VIEW("summary"));
  }, []);

  const {
    theme,
    amountState,
    paymentState,
    setPaymentState,
    fundingInstrument,
    setFundingInstrument,
  } = useStore();
  const { session } = useSessionStore();
  const {
    setComposeDraft,
    composeDraft,
    pendingFundingMethod,
    setPendingFundingMethod,
    pendingSourceCountry,
    setPendingSourceCountry,
    pendingDestinationCountry,
    setPendingDestinationCountry,
    pendingDestinationCurrency,
    setPendingDestinationCurrency,
  } = useRequestStore();
  const { redirectTo, linkId: linkIdFromRoute } = useRouteWithToken();
  const navigate = useNavigate();

  const linkId = linkIdFromRoute || session.data?.linkId;
  const userStatus = session.data?.userStatus;
  const canOriginate = !userStatus?.blocked && !userStatus?.suspended;
  // Revalidates on mount; refresh when opening the picker or after register.
  const {
    cards,
    bankAccounts,
    lastUsedFunding,
    loading: paymentMethodsLoading,
    refresh: refreshPaymentMethods,
  } = usePaymentMethods(linkId);
  const { amount, anchor: amountAnchor } = amountState;
  const colorConfig = theme.whiteView;
  const {
    background,
    backgroundBtn,
    textColorBtn,
    title,
    bodyCopy,
    dropzoneColors,
    bannerHighlightBackground,
    bannerHighlightColor,
    bannerHighlightIconColors,
  } = colorConfig as typeof colorConfig & {
    bannerHighlightBackground?: string;
    bannerHighlightColor?: string;
    bannerHighlightIconColors?: string[];
  };
  const partnerColors = session.data?.partner?.theme?.colors as
    | Record<string, string>
    | undefined;
  const accentSolid = accentInkOnSurface(partnerColors, background, bodyCopy);

  const [fundingMethod, setFundingMethodState] = useState<string | null>(
    pendingFundingMethod
  );
  const setFundingMethod = useCallback(
    (method: string | null) => {
      setFundingMethodState(method);
      setPendingFundingMethod(method);
    },
    [setPendingFundingMethod]
  );
  const [showFundingPopup, setShowFundingPopup] = useState(false);
  const [showOriginPopup, setShowOriginPopup] = useState(false);
  const [showDestinationPopup, setShowDestinationPopup] = useState(false);
  const [showExcratePopup, setShowExcratePopup] = useState(false);
  const [showRouteUnavailable, setShowRouteUnavailable] = useState(false);
  const [showAmountLimit, setShowAmountLimit] = useState(false);
  const [blockedFundingCard, setBlockedFundingCard] =
    useState<ICardPaymentMethod | null>(null);
  const [classChangedToast, setClassChangedToast] = useState<
    "DEBIT_CARD" | "CREDIT_CARD" | null
  >(null);
  const [shakeFunding, setShakeFunding] = useState(false);
  const [suspendCommitment, setSuspendCommitment] = useState(false);

  useEffect(() => {
    if (!suspendCommitment) return;
    const id = window.requestAnimationFrame(() => setSuspendCommitment(false));
    return () => window.cancelAnimationFrame(id);
  }, [suspendCommitment]);

  const config = session.data?.config;
  const prefilledFundingMethod = resolveFundingMethod(session.data?.userData);
  const prefilledInstrumentId = config?.prefilledFundingPaymentMethodId;
  const corridors = config?.corridors ?? [];
  const locale = session.data?.language || "en";

  const seededOrigin = seedOriginCountry(corridors, config?.defaultCorridor);
  const seededDestination = seedDestinationCountry(
    corridors,
    config?.defaultCorridor
  );

  // Prefer store / composeDraft so add-card (and recipient back) remounts keep
  // the corridor the user already picked instead of re-seeding defaults.
  const [sourceCountry, setSourceCountryState] = useState(
    () =>
      pendingSourceCountry ??
      composeDraft?.sourceCountry ??
      seededOrigin ??
      ""
  );
  const [destinationCountry, setDestinationCountryState] = useState(
    () =>
      pendingDestinationCountry ??
      composeDraft?.destinationCountry ??
      seededDestination ??
      ""
  );
  const setSourceCountry = useCallback(
    (code: string) => {
      setSourceCountryState(code);
      setPendingSourceCountry(code);
    },
    [setPendingSourceCountry]
  );
  const setDestinationCountry = useCallback(
    (code: string) => {
      setDestinationCountryState(code);
      setPendingDestinationCountry(code);
    },
    [setPendingDestinationCountry]
  );

  const originCodes = useMemo(() => {
    const fromCorridors = supportedOrigins(corridors);
    if (fromCorridors.length > 0) return fromCorridors;
    return seededOrigin ? [seededOrigin] : [];
  }, [corridors, seededOrigin]);

  const originCountries = useMemo(
    () => countryOptionsFromCodes(originCodes, locale),
    [originCodes, locale]
  );

  const destinationPairs = useMemo(
    () => expandCorridorDestinationOptions(corridors, sourceCountry, locale),
    [corridors, sourceCountry, locale]
  );

  const activeCorridor = useMemo(
    () => findCorridor(corridors, sourceCountry, destinationCountry),
    [corridors, sourceCountry, destinationCountry]
  );

  const sourceCurrency =
    sourceCountry === config?.defaultCorridor?.originCountryCode &&
    config?.sourceCurrency
      ? config.sourceCurrency
      : currencyForCountryCode(sourceCountry);

  const destinationCurrencyCodes = useMemo(() => {
    const fromPairs = destinationPairs
      .filter((p) => p.countryCode === destinationCountry)
      .map((p) => p.currencyCode);
    if (fromPairs.length > 0) return fromPairs;
    const fromCorridor = (activeCorridor?.destinationCurrencyCodes ?? []).filter(
      Boolean
    );
    if (fromCorridor.length > 0) {
      return [...new Set(fromCorridor.map((c) => c.toUpperCase()))];
    }
    if (
      destinationCountry === config?.defaultCorridor?.destinationCountryCode &&
      config?.destinationCurrency
    ) {
      return [config.destinationCurrency];
    }
    return destinationCountry ? [currencyForCountryCode(destinationCountry)] : [];
  }, [destinationPairs, destinationCountry, activeCorridor, config]);

  const pinForCurrentDest =
    destinationCountry === config?.defaultCorridor?.destinationCountryCode
      ? config?.destinationCurrency
      : undefined;

  const [destinationCurrency, setDestinationCurrencyState] = useState(() => {
    const restored =
      pendingDestinationCurrency ?? composeDraft?.destinationCurrency;
    if (restored && destinationCurrencyCodes.includes(restored)) {
      return restored;
    }
    return (
      pickDefaultDestinationCurrency(
        destinationCountry,
        destinationCurrencyCodes,
        pinForCurrentDest
      ) ?? destinationCurrencyCodes[0]
    );
  });
  const setDestinationCurrency = useCallback(
    (currency: string) => {
      setDestinationCurrencyState(currency);
      setPendingDestinationCurrency(currency);
    },
    [setPendingDestinationCurrency]
  );

  // Once corridors load, seed empty picks and clamp invalid destinations.
  // Prefer the portal-pinned defaultCorridor destination when it is still
  // valid for the current origin; otherwise fall back to the first supported.
  useEffect(() => {
    if (corridors.length === 0) return;
    if (!sourceCountry) {
      const origin = seedOriginCountry(corridors, config?.defaultCorridor);
      if (origin) setSourceCountry(origin);
      return;
    }
    const valid = supportedDestinations(corridors, sourceCountry);
    if (valid.length === 0) return;
    if (!destinationCountry || !valid.includes(destinationCountry)) {
      const pinned = config?.defaultCorridor?.destinationCountryCode;
      setDestinationCountry(
        pinned && valid.includes(pinned) ? pinned : valid[0]
      );
    }
  }, [
    corridors,
    sourceCountry,
    destinationCountry,
    config?.defaultCorridor,
    setSourceCountry,
    setDestinationCountry,
  ]);

  useEffect(() => {
    if (!destinationCurrencyCodes.includes(destinationCurrency)) {
      setDestinationCurrency(
        pickDefaultDestinationCurrency(
          destinationCountry,
          destinationCurrencyCodes,
          pinForCurrentDest
        ) ?? destinationCurrencyCodes[0]
      );
    }
  }, [
    destinationCurrencyCodes,
    destinationCurrency,
    destinationCountry,
    pinForCurrentDest,
    setDestinationCurrency,
  ]);

  const clearFundingSelection = useCallback(() => {
    setFundingMethod(null);
    setFundingInstrument(null);
    if (paymentState.payment != null) {
      setPaymentState({ payment: null });
    }
  }, [
    setFundingMethod,
    setFundingInstrument,
    paymentState.payment,
    setPaymentState,
  ]);

  const handleOriginChange = useCallback(
    (code: string) => {
      if (code !== sourceCountry) {
        clearFundingSelection();
        const validDestinations = supportedDestinations(corridors, code);
        if (
          validDestinations.length > 0 &&
          !validDestinations.includes(destinationCountry)
        ) {
          setDestinationCountry(validDestinations[0]);
        }
      }
      setSourceCountry(code);
      safeCapture(getEvents().ORIGIN_SELECTED, {
        from_country: code,
        to_country: destinationCountry,
      });
    },
    [sourceCountry, destinationCountry, corridors, clearFundingSelection]
  );

  const handleDestinationChange = useCallback(
    (countryCode: string, currencyCode: string) => {
      if (
        countryCode !== destinationCountry ||
        currencyCode !== destinationCurrency
      ) {
        clearFundingSelection();
      }
      setDestinationCountry(countryCode);
      setDestinationCurrency(currencyCode);
      safeCapture(getEvents().DESTINATION_SELECTED, {
        from_country: sourceCountry,
        to_country: countryCode,
        to_currency: currencyCode,
      });
    },
    [
      destinationCountry,
      destinationCurrency,
      sourceCountry,
      clearFundingSelection,
      setDestinationCountry,
      setDestinationCurrency,
    ]
  );

  const numericAmount = amount ? parseFloat(amount.replace(/,/g, "")) : NaN;
  const hasAmount = !isNaN(numericAmount) && numericAmount > 0;

  // vary=fundingMethod so each catalog rail (incl. CASH_DEPOSIT) survives
  // ranking — vary=payer collapses to the cheapest rail per payer and hid cash.
  // Facets are pre-collapse discovery (parity with flow-send).
  const preQuoteInput = useMemo<PreQuoteInput | null>(() => {
    if (!hasAmount) return null;
    return {
      originCountryCode: sourceCountry,
      destinationCountryCode: destinationCountry,
      amount: numericAmount,
      anchor: amountAnchor,
      destinationCurrency,
      sourceCurrency,
      vary: "fundingMethod",
      include: ["facets", "options", "summary"],
    };
  }, [
    hasAmount,
    numericAmount,
    amountAnchor,
    sourceCountry,
    destinationCountry,
    destinationCurrency,
    sourceCurrency,
  ]);

  const {
    result: preQuote,
    loading: preQuoteLoading,
    error: preQuoteError,
  } = usePreQuote(preQuoteInput);

  // Same corridor/amount, but vary=payer + pinned funding — mirrors the
  // backend mint gate so totalDebit covers the worst-case recipient payout.
  const commitmentPreQuoteInput = useMemo<PreQuoteInput | null>(() => {
    if (suspendCommitment || !hasAmount || !fundingMethod) return null;
    return {
      originCountryCode: sourceCountry,
      destinationCountryCode: destinationCountry,
      amount: numericAmount,
      anchor: amountAnchor,
      destinationCurrency,
      sourceCurrency,
      fundingMethod,
      vary: "payer",
    };
  }, [
    suspendCommitment,
    hasAmount,
    fundingMethod,
    numericAmount,
    amountAnchor,
    sourceCountry,
    destinationCountry,
    destinationCurrency,
    sourceCurrency,
  ]);

  const {
    result: commitmentPreQuote,
    loading: commitmentPreQuoteLoading,
    error: commitmentPreQuoteError,
  } = usePreQuote(commitmentPreQuoteInput);

  const fundingMethods = useMemo(
    () =>
      availableFundingMethodsFromDiscovery({
        facetFundingMethods: preQuote?.facets?.fundingMethods,
        options: preQuote?.options,
      }),
    [preQuote]
  );

  useEffect(() => {
    if (cardClassNoticeHandledRef.current) return;
    if (!cardClassNotice) return;
    if (cardClassNotice.kind === "changed" && fundingMethods.length === 0) {
      return;
    }

    cardClassNoticeHandledRef.current = true;
    navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: null,
    });

    if (cardClassNotice.kind === "unclassified") {
      setBlockedFundingCard(sheetCardFromNotice(cardClassNotice.card));
      return;
    }

    if (!fundingMethods.includes(cardClassNotice.fundingMethod)) {
      setPendingFundingMethod(null);
      setFundingMethod(null);
      setFundingInstrument(null);
      setPaymentState({ payment: null });
      setBlockedFundingCard(sheetCardFromNotice(cardClassNotice.card));
      return;
    }

    setClassChangedToast(cardClassNotice.fundingMethod);
  }, [
    cardClassNotice,
    fundingMethods,
    location.pathname,
    location.search,
    navigate,
    setFundingInstrument,
    setFundingMethod,
    setPaymentState,
    setPendingFundingMethod,
  ]);

  useEffect(() => {
    if (!railNotice) return;
    setBlockedFundingCard(railNotice.card);
    safeCapture(getEvents().CARD_UNAVAILABLE_SHOWN, {
      reason: railNotice.reason,
    });
    navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: null,
    });
  }, [railNotice, location.pathname, location.search, navigate]);

  const queriesForFundingMethod = useCallback(
    (method: FundingMethod) => {
      const discoveryOptions = preQuote?.options ?? [];
      const fundedOptions = commitmentPreQuote?.options ?? [];
      const customerFee = bestCustomerFeeForFundingMethod(
        method,
        preQuote?.facets,
        discoveryOptions.length > 0 ? discoveryOptions : fundedOptions
      );
      const corridors = corridorsForFundingMethod(method, {
        options: [...discoveryOptions, ...fundedOptions],
        facets: preQuote?.facets,
      });
      return skusForCorridors(method, corridors, {
        customerFee,
      });
    },
    [commitmentPreQuote?.options, preQuote]
  );

  const { details: fundingDetails, loading: fundingLimitsLoading } =
    useFundingMethodLimits(
      fundingMethods,
      queriesForFundingMethod,
      sourceCountry,
      destinationCountry,
      sourceCurrency
    );

  const selectedFundingOption = useMemo(() => {
    if (!fundingMethod || !preQuote) return undefined;
    return optionForFundingMethod(
      preQuote.options ?? [],
      fundingMethod as FundingMethod
    );
  }, [fundingMethod, preQuote]);

  const selectedFundingDetails = fundingMethod
    ? fundingDetails[fundingMethod as FundingMethod]
    : undefined;
  // D5a: same best as the picker highlight — never derived from totalDebit.
  const estimatedFee =
    fundingMethod != null ? (selectedFundingDetails?.fee ?? null) : null;
  const estimatedFeeDisplay =
    estimatedFee != null
      ? `$${amountFormatter(estimatedFee, sourceCurrency)}`
      : null;

  const sendForBound = hasAmount
    ? resolveComposeSendAmount({
        anchor: amountAnchor,
        pinnedAmount: numericAmount,
        optionSendAmount:
          selectedFundingOption?.sendAmount ??
          commitmentPreQuote?.summary.best?.sendAmount ??
          preQuote?.summary.best?.sendAmount ??
          null,
      })
    : null;
  const amountBoundVerdict = compareSendToBound(
    sendForBound,
    selectedFundingDetails
  );
  const amountOutOfRange =
    amountBoundVerdict === "below_min" || amountBoundVerdict === "above_max";

  const totalDebit = useMemo(() => {
    if (!hasAmount || !fundingMethod) return null;
    // Block confirm until the commitment estimate resolves — the cheapest
    // funding-rail fee undersizes the mint pre-flight (SEND_COMMITMENT_EXCEEDED).
    if (commitmentPreQuoteLoading || !commitmentPreQuote) return null;
    return maxTotalDebit(commitmentPreQuote.options ?? []);
  }, [
    hasAmount,
    fundingMethod,
    commitmentPreQuoteLoading,
    commitmentPreQuote,
  ]);

  const receiveRange = preQuote?.summary.receiveAmount;
  const receiveAmount =
    selectedFundingOption?.receiveAmount ??
    preQuote?.summary.best?.receiveAmount ??
    receiveRange?.avg ??
    null;
  const receiveAmountDisplay =
    preQuoteLoading && hasAmount
      ? "..."
      : receiveAmount != null
        ? amountFormatter(receiveAmount, destinationCurrency, {
            country: "en-US",
          })
        : undefined;

  const sendAmountDerived =
    selectedFundingOption?.sendAmount ??
    preQuote?.summary.best?.sendAmount ??
    null;

  const firstSend = useFirstSendWarning({
    linkId,
    sendAmount: sendAmountDerived,
    sourceCurrency,
    fundingMethod,
  });

  const sendAmountDisplay =
    preQuoteLoading && hasAmount
      ? "..."
      : sendAmountDerived != null
        ? amountFormatter(sendAmountDerived, sourceCurrency, {
            country: "en-US",
          })
        : undefined;

  // Empty funding rails after a successful pre-quote means no rail can quote
  // this send amount — over a method max, under a method min, or a dead zone
  // between rails (e.g. card fails margin above ~$555 while cash only clears
  // from ~$1300). Not the same as the destination country being disabled.
  const amountLimitUnavailable =
    !preQuoteLoading &&
    hasAmount &&
    Boolean(preQuote) &&
    fundingMethods.length === 0;
  const routeUnavailable =
    amountLimitUnavailable ||
    (!preQuoteLoading && hasAmount && Boolean(preQuoteError));
  const fundingPickerLoading =
    paymentMethodsLoading ||
    preQuoteLoading ||
    (fundingMethods.length === 0 && !routeUnavailable);

  useEffect(() => {
    if (routeUnavailable) setShowRouteUnavailable(true);
  }, [routeUnavailable]);

  const unavailableFundingMethod =
    fundingMethod ?? prefilledFundingMethod ?? null;

  const needsInstrument = fundingMethodRequiresInstrument(fundingMethod);
  const hasInstrument =
    Boolean(paymentState.payment?.cardId) ||
    (paymentState.payment?.type === paymentType.CARD &&
      isFundingInstrumentReady(fundingInstrument));
  const instrumentOk = !needsInstrument || hasInstrument;
  // Send parity: rainbow border while funding is the incomplete compose step.
  const fundingReady = Boolean(fundingMethod) && instrumentOk;
  const fundingPending = hasAmount && !fundingReady;

  const pickIcon = useCallback((method: string | null, brand?: string) => {
    const category = categoryFromFundingMethod(method);
    if (category === "CARD" && brand) {
      const upper = brand.toUpperCase();
      const logo = upper.includes("VISA")
        ? visaLogo
        : upper.includes("MASTERCARD")
          ? masterCardLogo
          : null;
      if (logo) return <img src={logo} alt={`${brand} logo`} />;
    }
    if (category === "ACCOUNT") return <BankIcon size={20} />;
    if (category === "CASH") return <CashSpecialIcon size={20} />;
    return <WalletIcon size={20} />;
  }, []);

  const fundingSummary = useMemo(() => {
    if (!fundingMethod) return t("transfer.selectFundingMethod");
    const methodLabel = t(`funding.methods.${fundingMethod}`, {
      defaultValue: fundingMethod,
    });
    if (needsInstrument && !hasInstrument) {
      return t("payment.selectMethod");
    }
    if (paymentState.payment?.brand) {
      return paymentState.payment.brand;
    }
    return methodLabel;
  }, [
    fundingMethod,
    needsInstrument,
    hasInstrument,
    paymentState.payment?.brand,
    t,
  ]);

  const canConfirm =
    hasAmount &&
    fundingReady &&
    totalDebit != null &&
    !routeUnavailable;
  // After Pay with is chosen, commitment pre-quote must resolve before
  // totalDebit / confirm are ready — mirror send's funded-quote loading CTA.
  const pricingLoading =
    (hasAmount && preQuoteLoading) ||
    (Boolean(fundingMethod) && commitmentPreQuoteLoading);
  const waitingOnPrice =
    hasAmount &&
    fundingReady &&
    !routeUnavailable &&
    (pricingLoading || totalDebit == null);
  const pricingTimedOut = useQuoteLoadingTimedOut(
    Boolean(waitingOnPrice && pricingLoading),
  );
  const priceFailed =
    pricingTimedOut ||
    Boolean(commitmentPreQuoteError) ||
    (hasAmount &&
      fundingReady &&
      !routeUnavailable &&
      !pricingLoading &&
      totalDebit == null);
  const blockForPricing = waitingOnPrice && !priceFailed;
  const showQuoteLoading = blockForPricing;
  // Send parity: rainbow CTA only when the compose is ready; the button itself
  // stays clickable so incomplete presses can nudge the missing field.
  const showRainbow =
    canConfirm && !fundingLimitsLoading && !amountOutOfRange;

  const nudgeIncompleteCompose = useCallback(() => {
    setShakeFunding(true);
    window.setTimeout(() => setShakeFunding(false), 500);
    const scrollToGap = () => {
      const target =
        document.querySelector(".payout-container .payment-required") ??
        document.querySelector(".payout-container .shake") ??
        document.querySelector(".estimated-quote-section");
      target?.scrollIntoView({ behavior: "smooth", block: "center" });
    };
    requestAnimationFrame(scrollToGap);
    window.setTimeout(scrollToGap, 50);
  }, []);

  const trySelectFundingInstrument = useCallback(
    (method: string): boolean => {
      const clearPaymentIfNeeded = () => {
        if (paymentState.payment != null) {
          setPaymentState({ payment: null });
        }
        setFundingInstrument(null);
      };

      if (!fundingMethodRequiresInstrument(method)) {
        clearPaymentIfNeeded();
        return true;
      }

      const category = categoryFromFundingMethod(method);
      if (!category || category === "CASH" || category === "OTHER") {
        clearPaymentIfNeeded();
        return true;
      }

      const selectCard = (card: ICardPaymentMethod) => {
        if (!isCardFundingAvailable(card, fundingMethods)) {
          return false;
        }
        setFundingInstrument({
          source: "saved",
          paymentMethodId: card.paymentMethodId,
          brand: card.brand,
          last4: card.last4,
        });
        setPaymentState({
          payment: {
            cardId: card.paymentMethodId,
            type: paymentType.CARD,
            brand: `${(card.brand || "").toUpperCase()}-${card.last4}`,
          },
        });
        return true;
      };

      const selectAccount = (account: {
        paymentMethodId: string;
        bankName: string;
        maskedAccountNumber: string;
      }) => {
        setFundingInstrument(null);
        setPaymentState({
          payment: {
            cardId: account.paymentMethodId,
            type: paymentType.BANK,
            brand: `${account.bankName} *${account.maskedAccountNumber.slice(-4)}`,
          },
        });
        return true;
      };

      if (category === "CARD") {
        if (prefilledInstrumentId) {
          const card = cards.find(
            (c) => c.paymentMethodId === prefilledInstrumentId
          );
          if (card) return selectCard(card);
        }

        const pmCard = session.data?.userData?.paymentMethod?.card;
        if (pmCard?.last4) {
          const brand = (pmCard.brand || "").toLowerCase();
          const card = cards.find(
            (c) =>
              c.last4 === pmCard.last4 &&
              (!brand || c.brand.toLowerCase() === brand)
          );
          if (card) return selectCard(card);
        }
      }

      if (category === "ACCOUNT") {
        if (prefilledInstrumentId) {
          const account = bankAccounts.find(
            (b) => b.paymentMethodId === prefilledInstrumentId
          );
          if (account) return selectAccount(account);
        }

        const pmBank = session.data?.userData?.paymentMethod?.bankAccount;
        const accountHint = pmBank?.accountNumber?.replace(/\*/g, "");
        if (accountHint && accountHint.length >= 4) {
          const suffix = accountHint.slice(-4);
          const account = bankAccounts.find((b) =>
            b.maskedAccountNumber.endsWith(suffix)
          );
          if (account) return selectAccount(account);
        }
      }

      if (lastUsedFunding?.paymentMethodId) {
        const lastCategory = categoryFromFundingMethod(lastUsedFunding.method);
        if (lastCategory === category) {
          if (lastCategory === "CARD") {
            const card = cards.find(
              (c) => c.paymentMethodId === lastUsedFunding.paymentMethodId
            );
            if (card) return selectCard(card);
          }
          if (lastCategory === "ACCOUNT") {
            const account = bankAccounts.find(
              (b) => b.paymentMethodId === lastUsedFunding.paymentMethodId
            );
            if (account) return selectAccount(account);
          }
        }
      }

      return false;
    },
    [
      bankAccounts,
      cards,
      fundingMethods,
      lastUsedFunding,
      paymentState.payment,
      prefilledInstrumentId,
      session.data?.userData?.paymentMethod,
      setFundingInstrument,
      setPaymentState,
    ]
  );

  const handleFundingPick = useCallback(
    (pick: FlatFundingPick) => {
      if (pick.kind === "add") {
        setFundingMethod(pick.method);
        setShowFundingPopup(false);
        safeCapture(getEvents().FUNDING_SELECTED, {
          funding_method: pick.method,
        });
        redirectTo(APP_ROUTES.ADD_CARD_PAGE);
        return;
      }
      if (pick.kind === "card") {
        const method = catalogFundingMethodOf(pick.card.fundingMethod);
        if (!method) {
          setBlockedFundingCard(pick.card);
          setShowFundingPopup(false);
          return;
        }
        setFundingMethod(method);
        setFundingInstrument({
          source: "saved",
          paymentMethodId: pick.card.paymentMethodId,
          brand: pick.card.brand,
          last4: pick.card.last4,
          fundingMethod: method,
        });
        setPaymentState({
          payment: {
            cardId: pick.card.paymentMethodId,
            type: paymentType.CARD,
            brand: `${(pick.card.brand || "").toUpperCase()}-${pick.card.last4}`,
          },
        });
        safeCapture(getEvents().FUNDING_SELECTED, { funding_method: method });
        safeCapture(getEvents().CARD_REGISTERED, {
          success: true,
          card_brand: pick.card.brand,
          funding_payment_method_id: pick.card.paymentMethodId,
          last4: pick.card.last4,
        });
        setShowFundingPopup(false);
        return;
      }
      if (pick.kind === "account") {
        const method =
          fundingMethods.find(
            (m) => m === "ACH" || m === "RTP" || m === "BANK_BALANCE"
          ) ?? "ACH";
        setFundingMethod(method);
        setFundingInstrument(null);
        setPaymentState({
          payment: {
            cardId: pick.account.paymentMethodId,
            type: paymentType.BANK,
            brand: `${pick.account.bankName} *${pick.account.maskedAccountNumber.slice(-4)}`,
          },
        });
        safeCapture(getEvents().FUNDING_SELECTED, { funding_method: method });
        setShowFundingPopup(false);
        return;
      }
      const method = pick.kind === "cash" ? "CASH_DEPOSIT" : pick.method;
      setFundingMethod(method);
      if (paymentState.payment != null) {
        setPaymentState({ payment: null });
      }
      setFundingInstrument(null);
      safeCapture(getEvents().FUNDING_SELECTED, { funding_method: method });
      setShowFundingPopup(false);
    },
    [
      fundingMethods,
      paymentState.payment,
      redirectTo,
      setFundingInstrument,
      setFundingMethod,
      setPaymentState,
    ]
  );

  // Partner pre-fill on the magic link (e.g. CASH_DEPOSIT / pinned card). Apply
  // silently — never open a popup (send does not auto-open funding UI either).
  useEffect(() => {
    if (routeUnavailable || fundingMethod || !prefilledFundingMethod) return;
    if (fundingMethods.length === 0) return;
    const pref = prefilledFundingMethod.toUpperCase();
    const match = fundingMethods.find((m) => m === pref);
    if (!match) return;

    setFundingMethodState(match);
    setPendingFundingMethod(match);
    if (fundingMethodRequiresInstrument(match)) {
      trySelectFundingInstrument(match);
    } else if (paymentState.payment != null) {
      setPaymentState({ payment: null });
      setFundingInstrument(null);
    }
  }, [
    prefilledFundingMethod,
    fundingMethods,
    fundingMethod,
    routeUnavailable,
    trySelectFundingInstrument,
    paymentState.payment,
    setPaymentState,
    setFundingInstrument,
    setPendingFundingMethod,
  ]);

  // Drop a selection that the refreshed pre-quote can no longer price.
  useEffect(() => {
    if (!fundingMethod || fundingMethods.length === 0) return;
    if (!fundingMethods.includes(fundingMethod as (typeof FUNDING_METHODS)[number])) {
      setFundingMethodState(null);
      if (paymentState.payment != null) {
        setPaymentState({ payment: null });
      }
      setFundingInstrument(null);
    }
  }, [
    fundingMethods,
    fundingMethod,
    paymentState.payment,
    setPaymentState,
    setFundingInstrument,
  ]);

  const handleFundingRowClick = () => {
    if (!hasAmount) return;
    void refreshPaymentMethods();
    setShowFundingPopup(true);
  };

  const fundingLocked = !hasAmount;

  const handleConfirm = useCallback(() => {
    if (!canOriginate) {
      redirectTo(APP_ROUTES.HOME_PAGE, { replace: true });
      return;
    }
    // Always allow the press: guide the user to whatever is still missing
    // instead of keeping the CTA disabled (send TransferPage parity).
    if (!hasAmount || !fundingReady) {
      // Only nudge — do not open funding/instrument pickers from the CTA.
      // Those open when the user taps Pay with.
      nudgeIncompleteCompose();
      return;
    }
    // Narrow for compose draft: fundingReady implies a method, but TS cannot.
    if (!fundingMethod) return;
    if (fundingLimitsLoading) return;
    if (amountOutOfRange) {
      setShowAmountLimit(true);
      return;
    }
    // Compose is complete — wait for commitment pricing of this fundingMethod.
    if (pricingLoading || routeUnavailable || totalDebit == null) return;

    if (paymentState.payment?.type === paymentType.CARD) {
      const cardForGate = resolveFundingCardFromState(
        paymentState.payment,
        fundingInstrument,
        cards
      );
      if (
        cardForGate &&
        isFundingCardBlocked(cardForGate, fundingMethods)
      ) {
        setBlockedFundingCard(cardForGate);
        return;
      }
    }

    const capabilityKey = selectedFundingOption?.capabilityKey;
    const savedFundingId =
      fundingInstrument?.source === "saved"
        ? fundingInstrument.paymentMethodId
        : paymentState.payment?.cardId ?? undefined;

    safeCapture(getEvents().CONTINUE_CLICK);
    if (capabilityKey) {
      safeCapture(getEvents().CAPABILITY_SELECTED, {
        capability_key: capabilityKey,
        funding_method: fundingMethod,
      });
    }

    const composeSendAmount = resolveComposeSendAmount({
      anchor: amountAnchor,
      pinnedAmount: numericAmount,
      optionSendAmount:
        selectedFundingOption?.sendAmount ??
        commitmentPreQuote?.summary.best?.sendAmount ??
        preQuote?.summary.best?.sendAmount,
    });

    setComposeDraft({
      sourceCountry,
      destinationCountry,
      sourceCurrency,
      destinationCurrency,
      sendAmount: composeSendAmount,
      totalDebit,
      fundingMethod,
      ...(capabilityKey ? { capabilityKey } : {}),
      ...(savedFundingId ? { fundingPaymentMethodId: savedFundingId } : {}),
    });
    setPendingFundingMethod(null);
    redirectTo(APP_ROUTES.RECIPIENT_PAGE);
  }, [
    canOriginate,
    fundingReady,
    fundingMethod,
    hasAmount,
    pricingLoading,
    routeUnavailable,
    totalDebit,
    fundingLimitsLoading,
    amountOutOfRange,
    nudgeIncompleteCompose,
    setComposeDraft,
    sourceCountry,
    destinationCountry,
    sourceCurrency,
    destinationCurrency,
    numericAmount,
    amountAnchor,
    selectedFundingOption,
    commitmentPreQuote,
    preQuote,
    fundingInstrument,
    paymentState.payment,
    cards,
    fundingMethods,
    redirectTo,
    setPendingFundingMethod,
  ]);

  const estimatedRate = preQuote?.summary.best?.customerFxRate;
  const rateText = estimatedRate
    ? t("exchange.rate", {
        from: sourceCurrency,
        rate: estimatedRate.toFixed(4),
        to: destinationCurrency,
      })
    : t("exchange.rate", {
        from: sourceCurrency,
        rate: "—",
        to: destinationCurrency,
      });
  const openFxDisclaimer = useCallback(() => setShowExcratePopup(true), []);
  const onFxDisclaimerKeyDown = useCallback((event: KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setShowExcratePopup(true);
    }
  }, []);

  const { openSupport, supportSheet } = useSupportSheet();

  if (showRouteUnavailable) {
    return (
      <RouteUnavailablePage
        destinationCountry={destinationCountry}
        reason="amount"
        fundingMethod={unavailableFundingMethod}
        onClose={() => setShowRouteUnavailable(false)}
      />
    );
  }

  return (
    <>
      <AuraLayout colorConfig={colorConfig} className="scrollable need-help-header">
          <Layout.Header>
            <Navbar
              title={t("common.needHelp")}
              align="right"
              onBackClick={() => navigate(-1)}
              onIconClick={() => openSupport()}
              icon={<SupportIcon size={35} colors={dropzoneColors} />}
            />
          </Layout.Header>
          <Layout.Content className="transfer-section">
            <ScrollFadeIndicator color={bodyCopy} />
            <section>
              <Title title={t("transfer.title")} align="left" color={TITLE_COLOR} />
              <section
                className={`estimated-quote-section stacked-quote${
                  shakeFunding && !hasAmount ? " shake" : ""
                }`}
              >
                <section className="quote-container">
                  <div className="banrural-amounts">
                    <div className="banrural-send-wrap">
                      <QuoteInfo
                        label={t("transfer.youSend")}
                        currency={sourceCurrency}
                        textColor={bodyCopy}
                        mode="input"
                        side="send"
                        isActive={amountAnchor === "send"}
                        derivedAmount={
                          amountAnchor === "receive" ? sendAmountDisplay : undefined
                        }
                        icon={<FlagCircle iso2={sourceCountry.toLowerCase()} />}
                        showSelectorChevron
                        onSelect={() => setShowOriginPopup(true)}
                      />
                      <span
                        className="banrural-swap"
                        role="button"
                        tabIndex={0}
                        onClick={openFxDisclaimer}
                        onKeyDown={onFxDisclaimerKeyDown}
                        style={{ background: backgroundBtn }}
                      >
                        <ExchangeIcon size={18} colors={[textColorBtn, textColorBtn]} />
                      </span>
                    </div>
                    <QuoteInfo
                      label={t("transfer.theyReceive")}
                      currency={destinationCurrency}
                      textColor={bodyCopy}
                      mode="input"
                      side="receive"
                      isActive={amountAnchor === "receive"}
                      derivedAmount={
                        amountAnchor === "send" ? receiveAmountDisplay : undefined
                      }
                      hideCurrencySymbol={destinationCurrency !== "USD"}
                      icon={<FlagCircle iso2={destinationCountry.toLowerCase()} />}
                      showSelectorChevron
                      onSelect={() => setShowDestinationPopup(true)}
                      bottomSlot={
                        <section
                          className="banrural-exchange-rate banrural-exchange-rate--alert"
                          role="button"
                          tabIndex={0}
                          onClick={openFxDisclaimer}
                          onKeyDown={onFxDisclaimerKeyDown}
                          style={
                            {
                              "--bgc": bannerHighlightBackground ?? bodyCopy,
                              "--colorc": solidStop(
                                bannerHighlightIconColors?.[0] ?? accentSolid,
                              ),
                              "--fillc": bannerHighlightColor ?? title,
                            } as CSSProperties
                          }
                        >
                          <span className="banrural-exchange-rate-icon">i</span>
                          <span className="banrural-exchange-rate-text">
                            <b>{rateText}</b>
                            <label>{t("exchange.estimatedRate")}</label>
                          </span>
                        </section>
                      }
                    />
                  </div>
                </section>
              </section>
              <section className="payout-container">
                <PayoutInfo
                  title={t("transfer.payWith")}
                  subtitle={
                    fundingLocked
                      ? t("transfer.selectFundingMethod")
                      : fundingSummary
                  }
                  icon={pickIcon(fundingMethod, paymentState.payment?.brand)}
                  background={backgroundBtn}
                  color={textColorBtn}
                  locked={fundingLocked}
                  onChange={handleFundingRowClick}
                  className={`${fundingPending ? "payment-required" : ""} ${
                    shakeFunding && !fundingReady ? "shake" : ""
                  }`.trim()}
                />
                {firstSend.visible ? (
                  <FirstSendWarning title={firstSend.title} body={firstSend.body} />
                ) : null}
                {routeUnavailable ? (
                  <BodyCopy className="footer-text" textColor={bodyCopy}>
                    <b>{t("transfer.routeUnavailableTitle")}</b>
                    <br />
                    {t("transfer.routeUnavailableHint")}
                  </BodyCopy>
                ) : (
                  <BodyCopy
                    className="footer-text transfer-timing-note"
                    textColor={TITLE_COLOR}
                  >
                    <b>{t("transfer.shareableLink")}</b>
                    {" "}
                    <span
                      className="transfer-timing-note-rest"
                      style={{ color: bodyCopy }}
                    >
                      {t("transfer.shareableLinkHint")}
                    </span>
                  </BodyCopy>
                )}
                {priceFailed && !routeUnavailable ? (
                  <>
                    <BodyCopy className="payment-error-text" role="alert">
                      {t("transfer.quoteError")}
                    </BodyCopy>
                    <Button
                      variant="cancel"
                      size="small"
                      color={bodyCopy}
                      onClick={() => setSuspendCommitment(true)}
                    >
                      {t("transfer.quoteRetry")}
                    </Button>
                  </>
                ) : null}
              </section>
            </section>
          </Layout.Content>
          <Layout.Footer>
            <section className="footer-section">
              <FooterDetailSummary
                heading={t("transfer.detail")}
                fee={t("transfer.fee", {
                  amount:
                    estimatedFeeDisplay ??
                    `$${amountFormatter(0, sourceCurrency)}`,
                  currency: sourceCurrency,
                })}
                total={t("transfer.totalPayment", {
                  amount:
                    totalDebit != null
                      ? `$${amountFormatter(totalDebit, sourceCurrency)}`
                      : `$${amountFormatter(0, sourceCurrency)}`,
                  currency: sourceCurrency,
                })}
                titleColor={TITLE_COLOR}
                textColor={TITLE_COLOR}
              />
              <div
                className={`button-wrapper ${showRainbow ? "rainbow-ready" : ""}`}
              >
                <Button
                  size="large"
                  background={backgroundBtn}
                  color={textColorBtn}
                  disabled={blockForPricing || priceFailed}
                  onClick={handleConfirm}
                >
                  {showQuoteLoading
                    ? t("transfer.loadingQuote")
                    : t("common.continue")}
                </Button>
              </div>
            </section>
          </Layout.Footer>
        </AuraLayout>
        {blockedFundingCard ? (
          <Popup visible onClose={() => setBlockedFundingCard(null)}>
            <UnavailableFundingCardSheet
              card={blockedFundingCard}
              available={fundingMethods}
              bodyCopy={bodyCopy}
              backgroundBtn={backgroundBtn}
              textColorBtn={textColorBtn}
              onDismiss={() => setBlockedFundingCard(null)}
              showHeaderClose={false}
              reason={
                blockedFundingCard
                  ? blockedRailCards.get(blockedFundingCard.paymentMethodId)
                  : undefined
              }
              onChangeMethod={
                blockedFundingCard &&
                blockedRailCards.has(blockedFundingCard.paymentMethodId)
                  ? () => {
                      setBlockedFundingCard(null);
                      setShowFundingPopup(true);
                    }
                  : undefined
              }
            />
          </Popup>
        ) : null}
        {classChangedToast ? (
          <Toast
            type="info"
            duration={4000}
            message={t(
              classChangedToast === "CREDIT_CARD"
                ? "payment.classChangedCredit"
                : "payment.classChangedDebit"
            )}
            onClose={() => setClassChangedToast(null)}
          />
        ) : null}
        <Popup visible={showFundingPopup} onClose={() => setShowFundingPopup(false)}>
          <SelectFundingMethodSlot
            selectedMethod={fundingMethod}
            selectedPaymentMethodId={
              fundingInstrument?.source === "saved"
                ? fundingInstrument.paymentMethodId
                : paymentState.payment?.cardId ?? null
            }
            available={fundingMethods}
            cards={cards}
            bankAccounts={bankAccounts}
            loading={fundingPickerLoading}
            details={fundingDetails}
            onPick={handleFundingPick}
            onClose={() => setShowFundingPopup(false)}
            blockedRailCards={blockedRailCards}
          />
        </Popup>
        <Popup visible={showOriginPopup} onClose={() => setShowOriginPopup(false)}>
          <SelectCountrySlot
            titleKey="transfer.selectOrigin"
            countries={originCountries}
            selected={sourceCountry}
            onSelect={handleOriginChange}
            onClose={() => setShowOriginPopup(false)}
          />
        </Popup>
        <Popup
          visible={showDestinationPopup}
          onClose={() => setShowDestinationPopup(false)}
        >
          <SelectDestinationCountrySlot
            countries={destinationPairs}
            selectedCountry={destinationCountry}
            selectedCurrency={destinationCurrency}
            onSelect={handleDestinationChange}
            onClose={() => setShowDestinationPopup(false)}
          />
        </Popup>
        <Popup
          visible={showAmountLimit && amountOutOfRange}
          onClose={() => setShowAmountLimit(false)}
        >
          <AmountOutOfRangeInfo
            kind={amountBoundVerdict === "below_min" ? "below_min" : "above_max"}
            method={t(`funding.methods.${fundingMethod ?? ""}`, {
              defaultValue: fundingMethod ?? "",
            })}
            bound={
              amountBoundVerdict === "below_min"
                ? (selectedFundingDetails?.minAmount ?? 0)
                : (selectedFundingDetails?.maxAmount ?? 0)
            }
            currency={sourceCurrency}
            onClose={() => setShowAmountLimit(false)}
          />
        </Popup>
        <Popup
          visible={showExcratePopup}
          onClose={() => setShowExcratePopup(false)}
        >
          <section className="qoute-popup">
            <WarningIcon size={60} />
            <BodyCopy textColor={bodyCopy}>
              <Trans i18nKey="transfer.quote.disclaimer" />
            </BodyCopy>
          </section>
        </Popup>
      {supportSheet}
    </>
  );
});
